import { useRef } from 'react';
import { MotionValue, motion, useTransform } from 'framer-motion';
import ArrowIcon from '@zola/zola-ui/src/components/SvgIcons/Arrow';
import HighlightTitle from '@zola/zola-ui/src/components/PreAuthV3/HighlightTitle';
import { RESPONSIVE_CLASSES } from '@zola/zola-ui/src/styles/emotion/mixins/responsive';
import { isMobile, isTablet, isXlDesktop } from '@zola-helpers/client/dist/es/util/responsive';

// Utils
import isClientSide from 'libs/client/utils/isClientSide';
import getSourceElements from 'libs/client/Image/getSourceElements';
import gridContent from './content';

import {
  ForegroundImage,
  Grid,
  GridBackgroundPicture,
  GridBox,
  GridDescription,
  GridHeader,
  GridImage,
  GridImageWrapper,
  GridSectionWrapper,
  Sidebar,
  Subtitle,
  TitleWrapper,
} from './PlanningGrid.styles';

type SolutionGridProps = {
  scrollYProgress: MotionValue<number>;
};

const PlanningGrid: React.FC<SolutionGridProps> = ({ scrollYProgress }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);

  let parallax = {
    keyframes: [0, 0.75],
    yRange: [0, -10],
  };

  if (isClientSide()) {
    if (isMobile()) {
      parallax = {
        keyframes: [0, 0.55],
        yRange: [0, -40],
      };
    }

    if (isTablet()) {
      parallax = {
        keyframes: [0, 0.65],
        yRange: [0, -30],
      };
    }

    if (isXlDesktop()) {
      parallax = {
        keyframes: [0, 0.5],
        yRange: [0, -20],
      };
    }
  }

  const y = useTransform(scrollYProgress, parallax.keyframes, parallax.yRange);

  const GridBoxes: JSX.Element[] = gridContent.map((gridItem, index) => (
    <GridBox
      key={`grid-box-${index}`}
      css={gridItem.excludeOnMobile ? RESPONSIVE_CLASSES.excludeMobile : null}
    >
      <GridHeader href={gridItem.href}>
        {gridItem.header}
        <ArrowIcon flip="horizontal" showTitle={false} />
      </GridHeader>
      <GridDescription>{gridItem.description}</GridDescription>
      <GridImageWrapper backgroundColor={gridItem.backgroundColor}>
        {gridItem.backgroundImages && (
          <motion.div style={{ y }}>
            <GridBackgroundPicture>
              {getSourceElements(gridItem.backgroundImages.webp, 'webp')}
              {getSourceElements(gridItem.backgroundImages.jpg, 'jpg')}
              <GridImage src={gridItem.backgroundImages.jpg[0].url} alt="" />
            </GridBackgroundPicture>
          </motion.div>
        )}
        <ForegroundImage src={gridItem.foregroundImageSrc} />
      </GridImageWrapper>
    </GridBox>
  ));

  return (
    <GridSectionWrapper ref={ref}>
      <Sidebar>
        <TitleWrapper>
          <HighlightTitle isH1={false}>
            <>
              <span>Planning tools</span> for all the days along the way
            </>
          </HighlightTitle>
          <Subtitle>
            Free, customizable tools and support that make wedding planning manageable.
          </Subtitle>
        </TitleWrapper>
      </Sidebar>
      <Grid>{GridBoxes}</Grid>
    </GridSectionWrapper>
  );
};

export default PlanningGrid;
